import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useWalletAddress } from '@/hooks/useWalletAddress';
import { formatCountdown } from '@/utils/formatCountdown';
import { lamportsToSol } from '@/utils/solana/lamportsToSol';
import {
  useContributePoints,
  usePointsLotterySeason,
} from '@/hooks/usePointsLottery';
import { useUserPoints } from '@/hooks/useUserPoints';

// Toggle to show/hide extra rewards banner
const SHOW_EXTRA_REWARDS_BANNER = false;

function formatNumber(value: string | number | undefined) {
  if (value === undefined) return '0';
  const numeric = typeof value === 'string' ? Number(value) : value;
  return Number.isNaN(numeric) ? '0' : numeric.toLocaleString();
}

export function LotteryContent() {
  const { address, isConnected } = useWalletAddress();
  const [selectedPoints, setSelectedPoints] = useState(0);
  const [inputValue, setInputValue] = useState<string>('0');
  const [countdownLabel, setCountdownLabel] = useState('--');
  const [hasManuallySet, setHasManuallySet] = useState(false);

  const seasonQuery = usePointsLotterySeason();
  const contributeMutation = useContributePoints();
  const { userPoints } = useUserPoints();

  // Sync input value with selectedPoints when it changes externally (e.g., from slider)
  useEffect(() => {
    setInputValue(selectedPoints.toString());
  }, [selectedPoints]);

  const season = seasonQuery.data?.season;
  const seasonError = seasonQuery.error;
  const isNoSeasonError = seasonError instanceof Error &&
    seasonError.message.toLowerCase().includes('no active season');
  const tiers = useMemo(() => {
    return (seasonQuery.data?.tiers ?? []).slice().sort((a, b) => a.unlockOrder - b.unlockOrder);
  }, [seasonQuery.data?.tiers]);

  const userSummary = seasonQuery.data?.user ?? null;

  // Set default selected points to half of available balance from self_point (only on initial mount)
  useEffect(() => {
    if (userPoints.self_point > 0 && selectedPoints === 0 && !hasManuallySet) {
      const available = userPoints.self_point;
      const halfBalance = Math.floor(available / 2);
      const defaultPoints = Math.max(100, Math.round(halfBalance / 100) * 100);
      setSelectedPoints(defaultPoints);
    }
  }, [userPoints.self_point, selectedPoints, hasManuallySet]);

  useEffect(() => {
    const target = season?.activeEndsAt;
    const interval = setInterval(() => {
      setCountdownLabel(formatCountdown(target ?? undefined));
    }, 1000);
    return () => clearInterval(interval);
  }, [season?.activeEndsAt]);

  const totalCommitted = useMemo(() => Number(season?.totalPointsCommitted ?? '0'), [season?.totalPointsCommitted]);

  // Calculate max possible win from CURRENTLY UNLOCKED tiers only
  const maxPossibleWinSOL = useMemo(() => {
    if (!tiers || tiers.length === 0) return 0;

    // Find all unlocked or currently available tiers (threshold <= totalCommitted)
    const unlockedTiers = tiers.filter((tier) =>
      Number(tier.thresholdPoints) <= totalCommitted
    );

    if (unlockedTiers.length === 0) return 0;

    // Find the maximum prize amount from unlocked tiers
    const maxPrizeLamports = Math.max(
      ...unlockedTiers.map((tier) => Number(tier.prizeAmountLamports))
    );

    return lamportsToSol(BigInt(maxPrizeLamports));
  }, [tiers, totalCommitted]);

  // All tier thresholds in order
  const TIER_THRESHOLDS = [
    0, 50_000_000, 100_000_000, 200_000_000, 500_000_000,
    800_000_000, 1_000_000_000, 1_500_000_000, 2_000_000_000,
    3_000_000_000, 4_000_000_000, 5_000_000_000, 6_500_000_000,
    8_000_000_000, 10_000_000_000, 15_000_000_000, 20_000_000_000,
  ];

  // Sliding window progress calculation
  const windowProgress = useMemo(() => {
    // Find which threshold we're currently past
    let currentTierIndex = 0;
    for (let i = TIER_THRESHOLDS.length - 1; i >= 0; i--) {
      if (totalCommitted >= TIER_THRESHOLDS[i]) {
        currentTierIndex = i;
        break;
      }
    }

    // If we've reached the final threshold, show full progress
    if (currentTierIndex === TIER_THRESHOLDS.length - 1) {
      return {
        windowStart: TIER_THRESHOLDS[currentTierIndex],
        windowEnd: TIER_THRESHOLDS[currentTierIndex],
        progress: 100,
        tierIndex: currentTierIndex,
        isComplete: true,
        nextTierThreshold: null,
      };
    }

    // Window is from current tier to next tier
    const windowStart = TIER_THRESHOLDS[currentTierIndex];
    const windowEnd = TIER_THRESHOLDS[currentTierIndex + 1];
    const progress = ((totalCommitted - windowStart) / (windowEnd - windowStart)) * 100;

    return {
      windowStart,
      windowEnd,
      progress: Math.min(Math.max(progress, 0), 100),
      tierIndex: currentTierIndex,
      isComplete: false,
      nextTierThreshold: windowEnd,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCommitted]);

  // Find next tier rewards to unlock
  const nextTierRewards = useMemo(() => {
    if (windowProgress.isComplete || !windowProgress.nextTierThreshold) return null;

    // Find tiers that unlock at the next threshold
    const nextThreshold = windowProgress.nextTierThreshold.toString();
    const unlockedTiers = tiers.filter(
      (tier) => tier.thresholdPoints === nextThreshold && !tier.isUnlocked
    );

    if (unlockedTiers.length === 0) return null;

    // Group by prize label
    const grouped = unlockedTiers.reduce((acc, tier) => {
      if (!acc[tier.prizeLabel]) {
        acc[tier.prizeLabel] = 0;
      }
      acc[tier.prizeLabel] += tier.quantity;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(grouped).map(([label, quantity]) => ({
      label,
      quantity,
    }));
  }, [tiers, windowProgress]);

  const handleContribute = async () => {
    if (!isConnected || !address) {
      toast.error('Connect your wallet to contribute points.');
      return;
    }

    if (isNoSeasonError) {
      toast.error('No active season. Please wait for the next season to start.');
      return;
    }

    if (!season) {
      toast.error('Season data unavailable. Please try again shortly.');
      return;
    }

    if (!Number.isFinite(selectedPoints) || selectedPoints <= 0) {
      toast.error('Enter a positive number of points.');
      return;
    }

    if (selectedPoints < 100) {
      toast.error('Minimum contribution is 100 points.');
      return;
    }

    if (selectedPoints % 100 !== 0) {
      toast.error('Points must be contributed in multiples of 100.');
      return;
    }

    // Store the contribution amount before resetting
    const contributionAmount = selectedPoints;

    // Optimistically reset slider to minimum immediately
    setSelectedPoints(100);
    setInputValue('100');

    try {
      await contributeMutation.mutateAsync(contributionAmount);
    } catch {
      // If the contribution fails, restore the previous value
      setSelectedPoints(contributionAmount);
      setInputValue(contributionAmount.toString());
    }
  };

  const availablePoints = userPoints.total_point;
  const sliderPercentage = availablePoints > 0 ? (selectedPoints / availablePoints) * 100 : 0;

  // Show error message if no season is active
  if (isNoSeasonError) {
    return (
      <div
        className="p-8 rounded-xl text-center"
        style={{
          border: '2px solid rgba(80, 228, 249, 0.3)',
          background: 'linear-gradient(90deg, rgba(81,228,248,0.1), rgba(1,74,92,0.4))',
        }}
      >
        <h2
          className="text-3xl md:text-4xl text-white mb-4"
          style={{ fontFamily: 'Hyperspace Race Capsule, cursive', fontWeight: 900 }}
        >
          No Active Season
        </h2>
        <p
          className="text-lg text-white/70 mb-6"
          style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500 }}
        >
          The Points Lottery is currently not active. A new season will start soon.
        </p>
        <p
          className="text-base text-white/60"
          style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400 }}
        >
          Keep earning points by opening chests and referring friends to be ready for the next season!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Extra Rewards Banner - negative margins to break out of parent px-4 padding */}
      {SHOW_EXTRA_REWARDS_BANNER && (
        <div className="-mx-4">
          <img
            src="/extra-reward-desktop.svg"
            alt={`Extra SOL Rewards for Season ${season?.seasonNo ?? ''}`}
            className="w-full h-auto"
          />
        </div>
      )}

      {/* Main Heading */}
      <div className="text-center space-y-3">
        <h1
          className="text-4xl md:text-5xl uppercase"
          style={{
            fontFamily: 'Hyperspace Race Capsule, cursive',
            fontWeight: 900,
            color: '#FFFFFF',
          }}
        >
          Join the Points Lottery
        </h1>
        <div className="text-3xl md:text-4xl" style={{ color: '#FFFFFF' }}>
          {maxPossibleWinSOL > 0 ? (
            <>
              <span
                style={{
                  fontFamily: 'Hyperspace Race Capsule, cursive',
                  fontWeight: 900,
                }}
              >
                WIN UP TO{' '}
              </span>
              <span
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 700,
                  color: '#FEBB01',
                }}
              >
                {maxPossibleWinSOL.toLocaleString('en-US', { maximumFractionDigits: 2 })} SOL
              </span>
            </>
          ) : (
            <span
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 700,
              }}
            >
              Win up to {maxPossibleWinSOL.toLocaleString('en-US', { maximumFractionDigits: 2 })} SOL
            </span>
          )}
        </div>
      </div>

      {/* Season Countdown Banner */}
      <div className="flex justify-center">
        <div
          className="inline-flex items-center gap-3 px-6 py-3 rounded-full"
          style={{
            background: '#262626',
            border: '2px solid #50E4F9',
          }}
        >
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                <path d="M10.5948 14.8696V11.1523M10.5948 7.43503H10.6042M19.9904 11.1523C19.9904 16.2848 15.7839 20.4455 10.5948 20.4455C5.40578 20.4455 1.19922 16.2848 1.19922 11.1523C1.19922 6.01982 5.40578 1.85913 10.5948 1.85913C15.7839 1.85913 19.9904 6.01982 19.9904 11.1523Z" stroke="#F3F3F3" strokeWidth="2.38967" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span
              className="text-base"
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 700,
                color: '#FFFFFF',
              }}
            >
              Season {season?.seasonNo ?? 0} - End:
            </span>
          </div>
          <span
            className="text-base"
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 700,
              color: '#51E4F8',
            }}
          >
            {countdownLabel}
          </span>
        </div>
      </div>

      {/* Progress Bar with Milestones - Sliding Window */}
      <div className="space-y-3 max-w-4xl mx-auto">
        {/* Current tier info */}
        <div className="text-center">
          <span
            className="text-sm"
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 600,
              color: '#50E4F9',
            }}
          >
            {windowProgress.isComplete ? (
              'All Tiers Unlocked! 🎉'
            ) : (
              `Tier ${windowProgress.tierIndex + 1} → Tier ${windowProgress.tierIndex + 2}`
            )}
          </span>
        </div>

        {/* Progress bar with milestones */}
        <div className="relative">
          {/* Progress bar - taller for better visibility */}
          <div className="h-6 rounded-full bg-white/10 overflow-hidden relative">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${windowProgress.progress}%`,
                background: 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
              }}
            />
          </div>

          {/* Milestone labels - showing current window range */}
          <div className="relative mt-3" style={{ height: '20px' }}>
            {!windowProgress.isComplete ? (
              <>
                {/* Start of window */}
                <span
                  className="text-xs absolute"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 600,
                    color: '#FFFFFF',
                    left: '0%',
                    transform: 'translateX(0%)',
                  }}
                >
                  {windowProgress.windowStart >= 1_000_000_000
                    ? `${(windowProgress.windowStart / 1_000_000_000).toFixed(1)}B`
                    : windowProgress.windowStart >= 1_000_000
                    ? `${(windowProgress.windowStart / 1_000_000).toFixed(0)}M`
                    : '0'}
                </span>

                {/* Current position - only show if not too close to edges */}
                {windowProgress.progress > 15 && windowProgress.progress < 85 && (
                  <span
                    className="text-xs absolute"
                    style={{
                      fontFamily: 'Outfit, sans-serif',
                      fontWeight: 700,
                      color: '#FEBB01',
                      left: `${windowProgress.progress}%`,
                      transform: 'translateX(-50%)',
                    }}
                  >
                    {totalCommitted >= 1_000_000_000
                      ? `${(totalCommitted / 1_000_000_000).toFixed(2)}B`
                      : totalCommitted >= 1_000_000
                      ? `${(totalCommitted / 1_000_000).toFixed(0)}M`
                      : totalCommitted.toLocaleString()}
                  </span>
                )}

                {/* End of window */}
                <span
                  className="text-xs absolute"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 600,
                    color: '#FFFFFF',
                    left: '100%',
                    transform: 'translateX(-100%)',
                  }}
                >
                  {windowProgress.windowEnd >= 1_000_000_000
                    ? `${(windowProgress.windowEnd / 1_000_000_000).toFixed(1)}B`
                    : windowProgress.windowEnd >= 1_000_000
                    ? `${(windowProgress.windowEnd / 1_000_000).toFixed(0)}M`
                    : windowProgress.windowEnd.toLocaleString()}
                </span>
              </>
            ) : (
              <span
                className="text-xs absolute"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 600,
                  color: '#FFFFFF',
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}
              >
                Maximum: 20B Points
              </span>
            )}
          </div>
        </div>

        {/* Total Points Display with Progress Info */}
        <div className="text-center pt-2 space-y-1">
          <span
            className="text-2xl inline-flex items-center gap-2"
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 700,
              color: '#EEB550',
            }}
          >
            <img src="/points.svg" alt="points" width={24} height={24} />
            {totalCommitted.toLocaleString()} points committed
          </span>
          {!windowProgress.isComplete && (
            <div
              className="text-sm"
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.7)',
              }}
            >
              {((windowProgress.windowEnd - totalCommitted) >= 1_000_000_000
                ? `${((windowProgress.windowEnd - totalCommitted) / 1_000_000_000).toFixed(2)}B`
                : ((windowProgress.windowEnd - totalCommitted) >= 1_000_000
                ? `${((windowProgress.windowEnd - totalCommitted) / 1_000_000).toFixed(0)}M`
                : (windowProgress.windowEnd - totalCommitted).toLocaleString())
              )} points until next tier unlocks
            </div>
          )}
        </div>

        {/* Next Tier Rewards Preview */}
        {nextTierRewards && nextTierRewards.length > 0 && (
          <div className="flex justify-center pt-4">
            <div
              className="px-6 py-3 rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, rgba(80, 228, 249, 0.15) 0%, rgba(123, 57, 238, 0.15) 100%)',
                border: '1px solid rgba(80, 228, 249, 0.3)',
              }}
            >
              <div className="flex items-center gap-3">
                <span
                  className="text-xs font-semibold uppercase"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    color: '#50E4F9',
                  }}
                >
                  Next Unlock:
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  {nextTierRewards.map((reward, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full"
                      style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      <span
                        className="text-sm font-bold"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          color: '#FEBB01',
                        }}
                      >
                        {reward.quantity}x
                      </span>
                      <span
                        className="text-sm font-semibold"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          color: '#FFFFFF',
                        }}
                      >
                        {reward.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invested Points Display */}
      <div className="flex justify-center py-6">
        <div
          style={{
            display: 'flex',
            width: '497px',
            height: '115px',
            padding: '6px 10px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            flexShrink: 0,
            borderRadius: '100px',
            border: '2px dashed #828282',
          }}
        >
          <span
            className="text-lg"
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 700,
              color: '#FFFFFF',
            }}
          >
            Invested{' '}
            <span className="inline-flex items-center gap-1" style={{ color: '#EEB550' }}>
              <img src="/points.svg" alt="points" width={18} height={18} />
              {formatNumber(userSummary?.contributedPoints)}
            </span>{' '}
            Points
          </span>
        </div>
      </div>

      {/* Investment Section */}
      <div className="space-y-6">
        {/* Condensed Slider with Input - All in dotted border */}
        <div className="flex items-center gap-3 px-4 py-2" style={{
          border: '1px dotted #50E4F9',
          borderRadius: '9999px',
        }}>
          {/* Slider */}
          <div className="flex-1">
            <input
              type="range"
              min="100"
              max={availablePoints}
              step="100"
              value={selectedPoints}
              disabled={contributeMutation.isPending}
              onChange={(e) => {
                const value = Number(e.target.value);
                const rounded = Math.round(value / 100) * 100;
                setSelectedPoints(rounded);
                setHasManuallySet(true);
              }}
              onInput={(e) => {
                const value = Number((e.target as HTMLInputElement).value);
                const rounded = Math.round(value / 100) * 100;
                setSelectedPoints(rounded);
                setHasManuallySet(true);
              }}
              className="w-full h-3 rounded-full appearance-none cursor-pointer slider disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: '#FFFFFF',
                backgroundImage:
                  'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                backgroundSize: `${sliderPercentage}% 100%`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'left center',
                borderRadius: '9999px',
                height: '10px',
                pointerEvents: contributeMutation.isPending ? 'none' : 'auto',
                transition: 'none',
              }}
            />
          </div>

          {/* Input Field with Indicator */}
          <div className="relative flex items-center gap-2">
            <input
              type="number"
              min={100}
              step={100}
              value={inputValue}
              disabled={contributeMutation.isPending}
              onChange={(event) => {
                const value = event.target.value;
                // Allow empty string - don't update selectedPoints yet
                if (value === '') {
                  setInputValue('');
                  setHasManuallySet(true);
                  return;
                }
                const numValue = Number(value);
                // Allow any number input - validation happens on submit
                if (!Number.isNaN(numValue) && numValue >= 0) {
                  setInputValue(value); // Keep user's input
                  setSelectedPoints(numValue);
                  setHasManuallySet(true);
                }
              }}
              onBlur={() => {
                // If input is empty on blur, set to 0
                if (inputValue === '') {
                  setInputValue('0');
                  setSelectedPoints(0);
                }
              }}
              className="w-28 px-2 py-1 rounded-lg border text-white text-center focus:outline-none focus:border-[#50E4F9] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 600,
                fontSize: '14px',
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                borderColor: selectedPoints > 0 && selectedPoints % 100 !== 0 ? '#ef4444' : 'rgba(255, 255, 255, 0.2)',
              }}
            />
            {selectedPoints > 0 && selectedPoints % 100 !== 0 && (
              <div
                className="absolute left-0 -bottom-6 px-2 py-1 rounded text-xs whitespace-nowrap"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  backgroundColor: 'rgba(239, 68, 68, 0.9)',
                  color: '#FFFFFF',
                  fontSize: '11px',
                }}
              >
                Must be multiple of 100
              </div>
            )}
          </div>

          {/* Label */}
          <span className="text-white font-semibold whitespace-nowrap" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '14px' }}>
            Points
          </span>
        </div>

        {/* Invest Button */}
        <div className="flex justify-center">
          <button
            onClick={handleContribute}
            disabled={contributeMutation.isPending}
            className="py-6 px-24 uppercase tracking-wide text-4xl transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              fontFamily: 'Hyperspace Race Capsule, cursive',
              fontWeight: 900,
              borderRadius: '100px',
              background: 'linear-gradient(250deg, #7239EE 16.62%, #C374F9 71.51%)',
              color: '#FFFFFF',
              boxShadow: '4px 4px 12px 0 rgba(0, 0, 0, 0.50)',
            }}
          >
            {contributeMutation.isPending ? 'Processing...' : 'Invest Points'}
          </button>
        </div>

        {/* Minimum investment requirement */}
        <div className="text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm border-2"
            style={{ borderColor: '#FFFFFF' }}
          >
            <div
              className="flex items-center justify-center rounded-full border-2"
              style={{
                width: '24px',
                height: '24px',
                borderColor: '#FFFFFF',
                backgroundColor: 'transparent',
              }}
            >
              <img src="/points.svg" alt="points" width={14} height={14} />
            </div>
            <span style={{ fontFamily: 'Outfit, sans-serif' }}>
              Investing requires at least 100 points
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
